import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ui } from "../lib/ui";

const SECTIONS = [
  { code: "eo_a", label: "Expression orale · A" },
  { code: "eo_b", label: "Expression orale · B" },
  { code: "ee_a", label: "Expression écrite · A" },
  { code: "ee_b", label: "Expression écrite · B" },
];
const BADGE = "inline-flex items-center justify-center bg-[#8B1A1A] text-[#F5EAE0] font-semibold text-xs px-2 py-0.5 rounded-full min-w-[2rem]";

export default function AdminTopicBank() {
  const { user, checked } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState("ee_a");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [newNum, setNewNum] = useState("");
  const [newText, setNewText] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");

  const loadCounts = useCallback(() => {
    api.get("/topics/sections").then((r) => {
      const m = {}; r.data.forEach((s) => { m[s.section] = s; }); setCounts(m);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get("/admin/topics", { params: { section, q } }).then((r) => setRows(r.data)).catch(() => {});
  }, [section, q]);

  useEffect(() => { if (user?.role === "admin") loadCounts(); }, [user, loadCounts]);
  useEffect(() => { if (user?.role === "admin") { const t = setTimeout(load, 200); return () => clearTimeout(t); } }, [user, load]);

  const add = async () => {
    if (!newNum || !newText.trim()) return toast.error("Enter a number and text.");
    try {
      await api.post("/admin/topics", { section, number: Number(newNum), text: newText });
      toast.success("Topic added"); setNewNum(""); setNewText(""); load(); loadCounts();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async (row) => {
    try {
      await api.put(`/admin/topics/${row.id}`, { section: row.section, number: row.number, text: editText });
      toast.success("Saved"); setEditId(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this topic?")) return;
    await api.delete(`/admin/topics/${id}`); load(); loadCounts();
  };

  if (!checked) return <div className="max-w-5xl mx-auto px-4 py-20 text-[#5A4441]">Loading…</div>;
  if (!user || user.role !== "admin")
    return <div className="max-w-md mx-auto px-4 py-20 text-center text-[#8B1A1A]">Admin only. <button onClick={() => navigate("/admin")} className="underline">Go to admin login</button></div>;

  const c = counts[section];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
      <button onClick={() => navigate("/admin")} className={ui.btnGhost + " mb-4 !px-0"}><ArrowLeft size={16} /> Back to moderation</button>
      <h1 className="font-serif text-3xl font-bold text-[#8B1A1A]">Topic bank</h1>
      <p className="text-sm text-[#5A4441] mt-1">View, search, add, edit and delete topics per section. Sorted by number.</p>

      <div className="flex flex-wrap gap-2 mt-6">
        {SECTIONS.map((s) => (
          <button key={s.code} onClick={() => setSection(s.code)} data-testid={`topicbank-tab-${s.code}`}
            className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 ${section === s.code ? "bg-[#8B1A1A] text-[#F5EAE0]" : "bg-[#FFFDF9] text-[#671813] border border-[#E8DCC8]"}`}>
            {s.label}<span className={`text-xs px-2 py-0.5 rounded-full ${section === s.code ? "bg-[#F5EAE0]/25" : "bg-[#F3E5D8]"}`}>{counts[s.code]?.count ?? 0}</span>
          </button>
        ))}
      </div>

      {c && <p className={ui.caption + " mt-3"}>{c.count} topics · numbers {c.min ?? "—"}–{c.max ?? "—"}</p>}

      {/* add */}
      <div className={ui.card + " p-4 mt-4 flex flex-col sm:flex-row gap-3 items-start"}>
        <input value={newNum} onChange={(e) => setNewNum(e.target.value)} type="number" placeholder="#" className={ui.input + " sm:w-24"} data-testid="topicbank-new-number" />
        <textarea value={newText} onChange={(e) => setNewText(e.target.value)} placeholder="New topic text" rows={2} className={ui.input + " flex-1"} data-testid="topicbank-new-text" />
        <button onClick={add} className={ui.btnPrimary} data-testid="topicbank-add-btn"><Plus size={16} /> Add</button>
      </div>

      {/* search */}
      <div className="relative mt-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B1A1A]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by number or keyword" className={ui.input + " !pl-9"} data-testid="topicbank-search" />
      </div>

      <div className="mt-4 space-y-2" data-testid="topicbank-list">
        {rows.map((r) => (
          <div key={r.id} className={ui.card + " p-3 flex items-start gap-3"}>
            <span className={BADGE + " mt-0.5"}>#{r.number}</span>
            {editId === r.id ? (
              <>
                <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} className={ui.input + " flex-1"} />
                <button onClick={() => saveEdit(r)} className={ui.btnGhost}><Save size={16} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-[#210201]">{r.text}</span>
                <button onClick={() => { setEditId(r.id); setEditText(r.text); }} className={ui.btnGhost + " text-xs"}>Edit</button>
                <button onClick={() => del(r.id)} className="text-[#8B1A1A] hover:text-[#671813] p-2"><Trash2 size={16} /></button>
              </>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className={ui.card + " p-8 text-center text-[#5A4441]"}>No topics in this section yet.</div>}
      </div>
    </div>
  );
}
